import React, { useState, useEffect } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  ArrowRight, ShieldCheck, Heart, Sparkles, Trophy, Calendar, 
  ChevronRight, Apple, HeartHandshake, BookOpen, Clock, Users, Flame 
} from "lucide-react";

import Navbar from "./components/Navbar";
import AuthModal from "./components/AuthModal";
import CheckoutModal from "./components/CheckoutModal";
import PrayerWall from "./components/PrayerWall";
import Dashboard from "./components/Dashboard";
import OnboardingFunnel from "./components/OnboardingFunnel";
import PaymentSuccess from "./components/PaymentSuccess";

export default function App() {
  const [user, setUser] = useState(null); // null (checking), false (unauthenticated), user object
  const [currentView, setCurrentView] = useState("home"); // 'home', 'programs', 'coaching', 'meals', 'prayer-wall', 'dashboard'
  
  // Modals state
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [authTab, setAuthTab] = useState("login");
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [checkoutProgram, setCheckoutProgram] = useState(null);
  const [paymentSessionId, setPaymentSessionId] = useState(null);

  // Marketing page data
  const [programs, setPrograms] = useState([]);
  const [todayWod, setTodayWod] = useState(null);

  // Anonymous coaching booking state
  const [anonName, setAnonName] = useState("");
  const [anonEmail, setAnonEmail] = useState("");
  const [anonDate, setAnonDate] = useState("");
  const [anonTime, setAnonTime] = useState("");
  const [anonNotes, setAnonNotes] = useState("");
  const [anonSuccess, setAnonSuccess] = useState("");
  const [anonError, setAnonError] = useState("");
  const [bookingSlots, setBookingSlots] = useState([]);

  // Check user session on mount
  const checkUserSession = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      setUser(false);
    }
  };

  // Fetch marketing data
  const fetchMarketingData = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      // Get programs
      const pRes = await axios.get(`${API_URL}/api/programs`);
      setPrograms(pRes.data);

      // Get WOD
      const wRes = await axios.get(`${API_URL}/api/spiritual/today`);
      setTodayWod(wRes.data);

      // Get coaching slots
      const sRes = await axios.get(`${API_URL}/api/coaching/slots`);
      setBookingSlots(sRes.data);
      if (sRes.data.length > 0) {
        setAnonTime(sRes.data[0]);
      }
    } catch (err) {
      console.error("Failed to load marketing data", err);
    }
  };

  useEffect(() => {
    checkUserSession();
    fetchMarketingData();

    // Detect Stripe redirect (success or cancel)
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("checkout");
    if (sessionId) {
      setPaymentSessionId(sessionId);
      setCurrentView("payment-success");
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (cancelled === "cancelled") {
      toast.info("Checkout cancelled. Your spot is still available when you're ready.");
      window.history.replaceState({}, document.title, window.location.pathname);
      setCurrentView("funnel");
    }
  }, []);

  // Start the Apply-to-Train onboarding funnel
  const startFunnel = () => {
    setCurrentView("funnel");
  };

  // Handle Logout
  const handleLogout = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(false);
      setCurrentView("home");
      toast.success("Successfully logged out of your Temple account.");
    } catch (err) {
      toast.error("Logout failed.");
    }
  };

  // Trigger Open Auth Modal
  const openAuthModal = (tab = "login") => {
    setAuthTab(tab);
    setIsAuthOpen(true);
  };

  // Trigger Stripe Checkout for a program purchase
  const handleOpenCheckout = async (program) => {
    if (!user) {
      toast.info("Please sign in or register to purchase this program");
      openAuthModal("register");
      return;
    }
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const { data } = await axios.post(
        `${API_URL}/api/programs/${program.id}/checkout`,
        { origin_url: window.location.origin },
        { withCredentials: true }
      );
      if (data.already_purchased) {
        toast.info(`${program.name} is already in your Temple Dashboard.`);
        setCurrentView("dashboard");
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
    }
  };

  // Handle Anonymous coaching form
  const handleAnonBooking = async (e) => {
    e.preventDefault();
    setAnonSuccess("");
    setAnonError("");

    if (!anonDate) {
      setAnonError("Please select a preferred date for your virtual session.");
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/coaching/book`, {
        client_name: anonName,
        email: anonEmail,
        preferred_date: anonDate,
        preferred_time: anonTime,
        notes: anonNotes
      });

      setAnonSuccess("Session inquiry sent! Instructor Stokes will review and approve your schedule shortly.");
      setAnonName("");
      setAnonEmail("");
      setAnonDate("");
      setAnonNotes("");
    } catch (err) {
      setAnonError("Booking submission failed. Please check your parameters.");
    }
  };

  // Next 14 calendar dates generator for scheduling (anonymous form)
  const getUpcomingDatesList = () => {
    const dates = [];
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    for (let i = 1; i <= 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const dy = String(d.getDate()).padStart(2, "0");
      dates.push({
        id: `${yr}-${mo}-${dy}`,
        label: `${months[d.getMonth()]} ${d.getDate()}`
      });
    }
    return dates;
  };
  const [upcomingDates] = useState(getUpcomingDatesList());

  // Full-screen funnel flow (no marketing chrome)
  if (currentView === "payment-success") {
    return (
      <>
        <Toaster position="bottom-right" theme="dark" />
        <PaymentSuccess
          sessionId={paymentSessionId}
          onContinue={() => { checkUserSession(); setCurrentView("dashboard"); }}
          onRetry={() => setCurrentView("home")}
        />
      </>
    );
  }

  if (currentView === "funnel") {
    return (
      <>
        <Toaster position="bottom-right" theme="dark" />
        <OnboardingFunnel
          user={user}
          onAuthSuccess={(userData) => setUser(userData)}
          onExit={() => setCurrentView("home")}
        />
      </>
    );
  }

  return (
    <div className="bg-black text-white min-h-screen font-manrope selection:bg-white selection:text-black">
      {/* Toast provider */}
      <Toaster position="bottom-right" theme="dark" />

      {/* Navigation Header */}
      <Navbar 
        user={user} 
        onOpenAuth={() => openAuthModal("login")} 
        onLogout={handleLogout}
        onNavigate={(view) => setCurrentView(view)}
        onStartFunnel={startFunnel}
      />

      {/* MAIN VIEWS SWITCHER */}
      <main className="pb-24">
        
        {/* VIEW 1: LANDING PAGE HOME */}
        {currentView === "home" && (
          <div className="space-y-24" data-testid="landing-container">
            {/* HERO SECTION - Bento Split Layout */}
            <section className="max-w-7xl mx-auto px-6 pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-6 space-y-8 text-left">
                <div className="inline-flex items-center gap-2 border border-neutral-800 px-3 py-1.5 text-xs uppercase tracking-widest text-neutral-400 bg-neutral-950 font-semibold">
                  <Flame className="w-3.5 h-3.5 text-white animate-pulse" />
                  <span>Faith-Based Athletic Conditioning</span>
                </div>

                <h1 className="font-bebas text-5xl sm:text-7xl lg:text-8xl font-bold tracking-tight leading-none uppercase">
                  Build Minds.<br />
                  Build Bodies.<br />
                  Edify <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-neutral-400 to-neutral-700">The Spirit</span>.
                </h1>

                <p className="text-sm sm:text-base text-neutral-400 font-light max-w-lg leading-relaxed">
                  ForgedByStokes is not another routine gym template. We are a community of men and women built on solid iron and sacred accountability. Together we sculpt muscles, sharpen minds, and anchor spirits.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  {user ? (
                    <button
                      onClick={() => setCurrentView("dashboard")}
                      className="bg-white text-black px-8 py-4 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-2"
                      data-testid="hero-dashboard-btn"
                    >
                      <span>Enter Your Temple Portal</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={startFunnel}
                      className="bg-white text-black px-8 py-4 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-2"
                      data-testid="hero-signup-btn"
                    >
                      <span>Apply To Train</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={() => {
                      const el = document.getElementById("mission");
                      el?.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="border border-neutral-800 hover:border-neutral-500 text-neutral-400 hover:text-white px-8 py-4 text-xs uppercase tracking-widest font-bold transition-all"
                  >
                    Read Our Creed
                  </button>
                </div>
              </div>

              {/* Hero Image Block (Noir photography as specified in design_guidelines) */}
              <div className="lg:col-span-6 relative aspect-[4/5] overflow-hidden border border-neutral-800 bg-neutral-950/20">
                <img 
                  src="/assets/coach-portrait.jpg" 
                  alt="Coach Michael Stokes"
                  className="w-full h-full object-cover object-top noir-img"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 border border-neutral-800 p-6 bg-black/80 backdrop-blur-md text-left">
                  <span className="text-[10px] uppercase font-mono tracking-widest text-neutral-500 font-bold block mb-1">
                    Featured Instructor
                  </span>
                  <h4 className="font-bebas text-2xl uppercase tracking-wider leading-none">
                    Michael Stokes
                  </h4>
                  <p className="text-xs text-neutral-400 mt-1">
                    {"\"I build strong physical temples to perform divine assignments with zero fatigue.\""}
                  </p>
                </div>
              </div>
            </section>

            {/* MARQUEE TEXT RUNNER - Brutalist scroller block */}
            <div className="bg-neutral-950 border-y border-neutral-900 py-6 overflow-hidden select-none">
              <div className="animate-marquee whitespace-nowrap flex items-center gap-8">
                {[1, 2, 3, 4].map((n) => (
                  <span 
                    key={n} 
                    className="font-bebas text-6xl sm:text-7xl md:text-8xl tracking-wider text-transparent stroke-text"
                    style={{ WebkitTextStroke: "1px #262626" }}
                  >
                    MIND • BODY • SPIRIT • STRENGTH • FAITH • COVENANT • TEMPLE • DISCIPLINE • IRON •
                  </span>
                ))}
              </div>
            </div>

            {/* OUR CREED & MISSION */}
            <section id="mission" className="max-w-7xl mx-auto px-6 py-12 scroll-mt-24">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5 border border-neutral-800 p-8 bg-neutral-950/40 relative">
                  <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-3 py-1 font-bebas text-xs text-neutral-500">
                    SCRIPTURE CORE
                  </div>
                  <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block mb-2 font-mono">
                    OUR RUNNING FOUNDATION
                  </span>
                  <blockquote className="italic text-lg sm:text-xl text-neutral-200 border-l-2 border-white pl-6 my-4 font-light font-manrope leading-relaxed">
                    {"\"For physical training is of some value, but godliness has value for all things, holding promise for both the present life and the life to come.\""}
                  </blockquote>
                  <span className="block text-right text-xs uppercase tracking-widest text-neutral-400 font-bold font-mono">
                    — 1 Timothy 4:8
                  </span>
                </div>

                <div className="lg:col-span-7 space-y-6 text-left">
                  <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block">
                    THE THREE PILLARS CREED
                  </span>
                  <h2 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
                    Minds, Bodies, & Spiritual Expansion
                  </h2>
                  <p className="text-sm text-neutral-400 leading-relaxed font-light font-manrope">
                    ForgedByStokes represents a complete integration. We do not ignore physical conditioning; we maximize it. But we recognize that a shredded body without a disciplined mind and a fortified spirit is an empty shell. We leverage hard resistance training, scientific macronutrient stewardship, and daily scripture accountability to build athletes capable of carrying heavy weights and heavy spiritual callings.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 text-left">
                    <div className="space-y-1.5">
                      <h4 className="font-bebas text-xl uppercase tracking-wider text-white">1. Disciplined Mind</h4>
                      <p className="text-xs text-neutral-500 leading-relaxed">Cultivating focus, grit, and resilience under extreme physical fatigue.</p>
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="font-bebas text-xl uppercase tracking-wider text-white">2. Capable Temple</h4>
                      <p className="text-xs text-neutral-500 leading-relaxed">Building muscular volume, explosive capacity, and cellular energy.</p>
                    </div>
                    <div className="space-y-1.5">
                      <h4 className="font-bebas text-xl uppercase tracking-wider text-white">3. Spiritual Fire</h4>
                      <p className="text-xs text-neutral-500 leading-relaxed">Deep scriptural engagement, prayers of steel, and community fellowship.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* INTEGRATED WOD DAILY DEVOTIONAL PREVIEW */}
            {todayWod && (
              <section className="max-w-4xl mx-auto px-6 py-12">
                <div className="border border-neutral-800 p-8 sm:p-12 bg-neutral-950/50 text-left space-y-8 relative">
                  <div className="absolute top-0 right-0 border-b border-l border-neutral-800 px-4 py-1.5 font-bebas text-xs text-neutral-400 bg-black">
                    {"TODAY'S DEVOTIONAL WOD"}
                  </div>

                  <div className="space-y-2">
                    <span className="text-[10px] uppercase font-mono tracking-widest text-neutral-500 font-bold block">
                      {"SCRIPTURE BULWARK: "}{todayWod.scripture}
                    </span>
                    <blockquote className="italic text-base sm:text-lg text-neutral-200 border-l-2 border-white pl-4 my-2 font-light">
                      {"\" "}{todayWod.verse}{" \""}
                    </blockquote>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-bebas text-3xl uppercase tracking-wide">
                      {todayWod.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-neutral-400 leading-relaxed font-light">
                      {todayWod.message}
                    </p>
                  </div>

                  <div className="border-t border-neutral-900 pt-6 space-y-3">
                    <span className="text-[10px] uppercase font-bold text-neutral-400 tracking-widest block font-mono">
                      {"TODAY'S WORKOUT OF THE DAY"}
                    </span>
                    <p className="text-xs sm:text-sm font-mono text-white font-bold bg-neutral-900 border border-neutral-800 p-4 leading-relaxed">
                      {todayWod.wod}
                    </p>
                    <p className="text-[10px] text-neutral-500 uppercase tracking-widest">
                      * Sign in to complete your workout logs and reflection journal entries!
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* PREMADE PROGRAMS PREVIEW GRID */}
            <section className="max-w-7xl mx-auto px-6 py-12 space-y-12">
              <div className="text-center space-y-2">
                <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block">
                  Iron Templates Store
                </span>
                <h2 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
                  Premade Program Blueprints
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {programs.map((prog) => (
                  <div 
                    key={prog.id} 
                    className="border border-neutral-800 bg-neutral-950/10 hover:border-neutral-700 transition-all flex flex-col justify-between"
                    data-testid={`program-card-${prog.id}`}
                  >
                    {/* Program Image (grayscale as required) */}
                    <div className="aspect-[16/10] overflow-hidden border-b border-neutral-800 relative bg-neutral-900">
                      <img 
                        src={prog.image_url} 
                        alt={prog.name}
                        className="w-full h-full object-cover noir-img"
                      />
                      <span className="absolute top-4 left-4 text-[9px] uppercase font-bold tracking-widest bg-white text-black px-2 py-0.5">
                        {prog.category}
                      </span>
                    </div>

                    {/* Program Content */}
                    <div className="p-6 text-left flex-grow space-y-3">
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="text-[10px] uppercase font-mono text-neutral-500">{prog.duration} Blueprint</span>
                        <span className="font-bebas text-2xl font-bold">${prog.price}</span>
                      </div>
                      <h4 className="font-bebas text-2xl uppercase tracking-wide leading-none text-white">{prog.name}</h4>
                      <p className="text-xs text-neutral-400 font-manrope font-light leading-relaxed">
                        {prog.description}
                      </p>
                    </div>

                    {/* Purchase Action Button */}
                    <div className="p-6 pt-0">
                      <button
                        onClick={() => handleOpenCheckout(prog)}
                        className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-1.5"
                        data-testid={`program-purchase-button-${prog.id}`}
                      >
                        <span>Buy Blueprint</span>
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 1-ON-1 VIRTUAL COACHING SECTION WITH INLINE SCHEDULER FORM */}
            <section className="max-w-7xl mx-auto px-6 py-12 border-t border-neutral-900">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Info and Creed */}
                <div className="lg:col-span-5 space-y-6 text-left">
                  <div className="inline-block border border-white p-2">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block">
                    Custom Virtual Coaching
                  </span>
                  <h2 className="font-bebas text-4xl sm:text-5xl uppercase tracking-wide leading-none">
                    1-on-1 Virtual Sessions with Stokes
                  </h2>
                  <p className="text-sm text-neutral-400 leading-relaxed font-light">
                    Need absolute custom accountability? Book 1-on-1 coaching sessions. Michael Stokes will analyze your lifting mechanics, correct nutritional errors, and provide dedicated mental coaching to lift heavy loads and honor your temple.
                  </p>
                  <ul className="text-xs text-neutral-500 space-y-2 uppercase font-mono tracking-wider font-semibold">
                    <li className="flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-white" />
                      <span>Form & Mechanics Breakdown</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Apple className="w-4 h-4 text-white" />
                      <span>Macro & Meal adjustments</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-white" />
                      <span>Dedicated Spiritual accountability</span>
                    </li>
                  </ul>
                </div>

                {/* Booking Form (Anon or authenticated user) */}
                <div className="lg:col-span-7 border border-neutral-800 p-8 bg-neutral-950/20 text-left" data-testid="anon-booking-form-container">
                  <h3 className="font-bebas text-2xl uppercase tracking-wider mb-6">Virtual session reservation inquiry</h3>

                  {anonSuccess && (
                    <div 
                      className="mb-4 border border-white text-white px-4 py-3 text-xs uppercase tracking-wider font-semibold"
                      data-testid="anon-booking-success"
                    >
                      {anonSuccess}
                    </div>
                  )}

                  {anonError && (
                    <div 
                      className="mb-4 bg-white text-black px-4 py-3 text-xs uppercase tracking-wider font-semibold"
                      data-testid="anon-booking-error"
                    >
                      {anonError}
                    </div>
                  )}

                  <form onSubmit={handleAnonBooking} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Full Name</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Elijah Shepherd"
                          value={anonName}
                          onChange={(e) => setAnonName(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none"
                          data-testid="anon-booking-name"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Email Address</label>
                        <input
                          type="email"
                          required
                          placeholder="e.g. steward@temple.com"
                          value={anonEmail}
                          onChange={(e) => setAnonEmail(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none"
                          data-testid="anon-booking-email"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Date list selection */}
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Preferred Date</label>
                        <select
                          required
                          value={anonDate}
                          onChange={(e) => setAnonDate(e.target.value)}
                          className="w-full bg-neutral-950 border-b border-neutral-800 py-2 text-xs text-white uppercase focus:outline-none"
                          data-testid="anon-booking-date"
                        >
                          <option value="">Select Preferred Date</option>
                          {upcomingDates.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.id} ({item.label})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Time slot selection */}
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Preferred Time</label>
                        <select
                          required
                          value={anonTime}
                          onChange={(e) => setAnonTime(e.target.value)}
                          className="w-full bg-neutral-950 border-b border-neutral-800 py-2 text-xs text-white focus:outline-none"
                          data-testid="anon-booking-time"
                        >
                          {bookingSlots.map((s, idx) => (
                            <option key={idx} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Brief session request goals</label>
                      <textarea
                        rows={2}
                        placeholder="Briefly share your physical/spiritual goals for this virtual session..."
                        value={anonNotes}
                        onChange={(e) => setAnonNotes(e.target.value)}
                        className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none resize-none"
                        data-testid="anon-booking-notes"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                      data-testid="anon-booking-submit"
                    >
                      Submit Virtual Booking Inquiry
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* VIEW 2: ALL PREMADE PROGRAMS PAGE */}
        {currentView === "programs" && (
          <div className="max-w-7xl mx-auto px-6 py-12 text-left space-y-12">
            <div className="space-y-2">
              <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block font-mono">
                SSA CATALOGUE BLUEPRINTS
              </span>
              <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl uppercase tracking-wide leading-none">
                Training Programs Store
              </h1>
              <p className="text-sm text-neutral-400 font-light leading-relaxed max-w-lg pt-2">
                Battle-tested blueprints engineered by Coach Michael Stokes. Real discipline, real iron, real results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {programs.map((prog) => (
                <div 
                  key={prog.id} 
                  className="border border-neutral-800 bg-neutral-950/10 hover:border-neutral-700 transition-all flex flex-col justify-between"
                  data-testid={`program-card-${prog.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden border-b border-neutral-800 bg-neutral-900">
                    <img 
                      src={prog.image_url} 
                      alt={prog.name}
                      className="w-full h-full object-cover noir-img"
                    />
                    <span className="absolute top-4 left-4 text-[9px] uppercase font-bold tracking-widest bg-white text-black px-2 py-0.5">
                      {prog.category}
                    </span>
                  </div>

                  <div className="p-6 flex-grow space-y-3">
                    <div className="flex justify-between items-baseline gap-2">
                      <span className="text-[10px] uppercase font-mono text-neutral-500">{prog.duration} Blueprint</span>
                      <span className="font-bebas text-2xl font-bold">${prog.price}</span>
                    </div>
                    <h4 className="font-bebas text-2xl uppercase tracking-wide leading-none text-white">{prog.name}</h4>
                    <p className="text-xs text-neutral-400 font-manrope font-light leading-relaxed">
                      {prog.description}
                    </p>
                  </div>

                  <div className="p-6 pt-0">
                    <button
                      onClick={() => handleOpenCheckout(prog)}
                      className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all flex items-center justify-center gap-1.5"
                      data-testid={`program-purchase-button-${prog.id}`}
                    >
                      <span>Secure Blueprint</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* VIEW 3: COACHING INFORMATION */}
        {currentView === "coaching" && (
          <div className="max-w-7xl mx-auto px-6 py-12 text-left space-y-12">
            <div className="space-y-2">
              <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block font-mono">
                SACRED ACCOUNTABILITY
              </span>
              <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl uppercase tracking-wide leading-none">
                1-on-1 Virtual Coaching
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5 space-y-6">
                <div className="aspect-[4/5] overflow-hidden border border-neutral-800 bg-neutral-900">
                  <img
                    src="/assets/coach-fullbody.jpg"
                    alt="Coach Michael Stokes training"
                    className="w-full h-full object-cover noir-img"
                  />
                </div>
                <p className="text-sm text-neutral-400 leading-relaxed font-light">
                  A personal temple is not built on random gym cards. It requires targeted loading parameters, physical form perfection, and consistent scriptural alignment. Michael Stokes works directly with standard clients to engineer complete progress protocols.
                </p>
                <div className="border border-neutral-800 p-6 bg-neutral-950/30">
                  <h4 className="font-bebas text-lg uppercase tracking-wide text-white mb-2">{"What's Included"}</h4>
                  <ul className="text-xs text-neutral-500 space-y-2 uppercase font-mono tracking-wider">
                    <li className="flex items-center gap-2 text-neutral-400">
                      <Trophy className="w-4 h-4 text-white" />
                      <span>Form mechanical breakdown live</span>
                    </li>
                    <li className="flex items-center gap-2 text-neutral-400">
                      <Apple className="w-4 h-4 text-white" />
                      <span>Dynamic macronutrient layouts</span>
                    </li>
                    <li className="flex items-center gap-2 text-neutral-400">
                      <Sparkles className="w-4 h-4 text-white" />
                      <span>Spiritual reflection and devotional journal checks</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Booking form */}
              <div className="lg:col-span-7 border border-neutral-800 p-8 bg-neutral-950/20">
                <h3 className="font-bebas text-2xl uppercase tracking-wider mb-6">Reservation Inquiry Scheduler</h3>
                {user ? (
                  <div className="space-y-4 text-center py-6">
                    <p className="text-xs uppercase tracking-widest text-neutral-400">You are logged in as {user.name}</p>
                    <button
                      onClick={() => setCurrentView("dashboard")}
                      className="bg-white text-black px-6 py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all"
                    >
                      Go To Your Dashboard Scheduler
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleAnonBooking} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Full Name</label>
                        <input
                          type="text"
                          required
                          placeholder="e.g. Elijah Shepherd"
                          value={anonName}
                          onChange={(e) => setAnonName(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Email Address</label>
                        <input
                          type="email"
                          required
                          placeholder="e.g. steward@temple.com"
                          value={anonEmail}
                          onChange={(e) => setAnonEmail(e.target.value)}
                          className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Preferred Date</label>
                        <select
                          required
                          value={anonDate}
                          onChange={(e) => setAnonDate(e.target.value)}
                          className="w-full bg-neutral-950 border-b border-neutral-800 py-2 text-xs text-white uppercase focus:outline-none"
                        >
                          <option value="">Select Preferred Date</option>
                          {upcomingDates.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.id} ({item.label})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Preferred Time</label>
                        <select
                          required
                          value={anonTime}
                          onChange={(e) => setAnonTime(e.target.value)}
                          className="w-full bg-neutral-950 border-b border-neutral-800 py-2 text-xs text-white focus:outline-none"
                        >
                          {bookingSlots.map((s, idx) => (
                            <option key={idx} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase tracking-widest text-neutral-400 block font-semibold">Brief goals note</label>
                      <textarea
                        rows={2}
                        placeholder="Goals for this session..."
                        value={anonNotes}
                        onChange={(e) => setAnonNotes(e.target.value)}
                        className="w-full bg-transparent border-b border-neutral-800 py-1.5 text-xs text-white rounded-none focus:border-white focus:outline-none resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-white text-black py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all duration-300"
                    >
                      Submit Booking Inquiry
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VIEW 4: MEAL PLAN CREDOS INFORMATION */}
        {currentView === "meals" && (
          <div className="max-w-7xl mx-auto px-6 py-12 text-left space-y-12">
            <div className="space-y-2">
              <span className="text-[10px] uppercase font-bold text-neutral-500 tracking-widest block font-mono">
                TEMPLE DIETETICS
              </span>
              <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl uppercase tracking-wide leading-none">
                Macronutrient Meal Plans
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 relative aspect-[16/10] overflow-hidden border border-neutral-800 bg-neutral-900">
                <img 
                  src="https://images.pexels.com/photos/4963581/pexels-photo-4963581.jpeg" 
                  alt="Minimal Food Bowl" 
                  className="w-full h-full object-cover noir-img"
                />
              </div>

              <div className="lg:col-span-6 space-y-6">
                <h3 className="font-bebas text-3xl uppercase tracking-wide">Sacred stewardship of macros</h3>
                <p className="text-sm text-neutral-400 leading-relaxed font-light">
                  Eating is an act of stewardship, not just immediate fuel consumption. We engineer custom macronutrient profiles (protein, carbs, fat ratios) that match your weight class targets and target metabolic thresholds. Each meal is designed to maximize recovery speeds.
                </p>
                
                <div className="flex gap-4">
                  {user ? (
                    <button
                      onClick={() => setCurrentView("dashboard")}
                      className="bg-white text-black px-6 py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all"
                    >
                      Go request Your custom Layout
                    </button>
                  ) : (
                    <button
                      onClick={() => openAuthModal("register")}
                      className="bg-white text-black px-6 py-3 text-xs uppercase tracking-widest font-extrabold hover:bg-neutral-200 transition-all"
                    >
                      Join Portal & Request plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* VIEW 5: INTEGRATED PUBLIC PRAYER WALL */}
        {currentView === "prayer-wall" && (
          <PrayerWall 
            user={user} 
            onOpenAuth={() => openAuthModal("login")} 
          />
        )}

        {/* VIEW 6: PROTECTED DASHBOARD AND CLIENT PORTAL */}
        {currentView === "dashboard" && (
          <Dashboard 
            user={user} 
            onOpenAuth={() => openAuthModal("login")} 
          />
        )}

      </main>

      {/* FOOTER BRUTALIST GRID */}
      <footer className="border-t border-neutral-900 bg-neutral-950/40 text-neutral-500 py-16 text-sm font-manrope">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12 text-left">
          
          {/* Brand Col */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img
                src="/assets/ssa-logo-white.png"
                alt="ForgedByStokes"
                className="h-20 w-auto"
              />
            </div>
            <p className="text-xs text-neutral-500 leading-relaxed font-light">
              Minds, Temple Bodies, and Spiritual growth. Engineered on solid iron and sacred covenants.
            </p>
          </div>

          {/* Links 1 */}
          <div className="space-y-3">
            <h5 className="font-bebas text-white uppercase tracking-wider text-sm">Services</h5>
            <ul className="text-xs uppercase tracking-widest space-y-2">
              <li><button onClick={() => setCurrentView("programs")} className="hover:text-white transition-colors focus:outline-none">Premade templates</button></li>
              <li><button onClick={() => setCurrentView("coaching")} className="hover:text-white transition-colors focus:outline-none">Virtual 1-on-1 sessions</button></li>
              <li><button onClick={() => setCurrentView("meals")} className="hover:text-white transition-colors focus:outline-none">Macronutrient meal layouts</button></li>
            </ul>
          </div>

          {/* Links 2 */}
          <div className="space-y-3">
            <h5 className="font-bebas text-white uppercase tracking-wider text-sm">Community</h5>
            <ul className="text-xs uppercase tracking-widest space-y-2">
              <li><button onClick={() => setCurrentView("prayer-wall")} className="hover:text-white transition-colors focus:outline-none">Community prayer wall</button></li>
              <li><button onClick={() => openAuthModal("register")} className="hover:text-white transition-colors focus:outline-none">Join fellowship portal</button></li>
            </ul>
          </div>

          {/* Disclaimer */}
          <div className="space-y-3">
            <h5 className="font-bebas text-white uppercase tracking-wider text-sm">Creed Legal</h5>
            <p className="text-[10px] text-neutral-600 leading-relaxed font-light">
              * ForgedByStokes is a faith-integrated brand. Payments are securely processed by Stripe. Built with pride under the emergent cluster platform, 2026.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 border-t border-neutral-900 pt-8 mt-12 flex flex-col sm:flex-row justify-between items-center text-xs text-neutral-600 gap-4">
          <span>ForgedByStokes © 2026. All rights reserved in covenant.</span>
          <span>Designed with high-contrast brutalist Monochromatic Noir templates.</span>
        </div>
      </footer>

      {/* SHARED MODALS */}
      <AuthModal 
        isOpen={isAuthOpen} 
        onClose={() => setIsAuthOpen(false)} 
        initialTab={authTab}
        onAuthSuccess={(userData) => {
          setUser(userData);
          toast.success(`Welcome back to the Temple, ${userData.name}!`);
          setCurrentView("dashboard");
        }}
      />

      {/* Program purchases now use real Stripe Checkout (redirect flow) */}
    </div>
  );
}
